/**
 * KPIStat — Standardized KPI display component
 *
 * Usage:
 *   <KPIStat value={42} label="Students" />
 *   <KPIStat value="$1,240" label="Revenue" trend="+12%" />
 *   <KPIStat value={7} label="Active Courses" variant="hero" />
 */
import React from "react";
import { Stack, Typography, Box } from "@mui/material";
import { motion } from "framer-motion";

const KPIStat = ({ value, label, trend, delay = 0, variant = "default" }) => {
  const isPositiveTrend = trend && !trend.startsWith("-");

  if (variant === "hero") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay, duration: 0.35 }}
      >
        <Stack alignItems="center" spacing={0.3} sx={{ minWidth: 64 }}>
          <Typography
            sx={{
              color: "var(--text-primary)",
              fontWeight: 700,
              fontSize: "1.2rem",
              fontFamily: "'Inter', sans-serif",
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>
          <Typography
            sx={{
              color: "color-mix(in srgb, var(--text-primary) 35%, transparent)",
              fontSize: "0.6rem",
              fontFamily: "'Inter', sans-serif",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontWeight: 500,
            }}
          >
            {label}
          </Typography>
        </Stack>
      </motion.div>
    );
  }

  // Default: inline pill style (for teacher dashboard, admin, etc.)
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 2,
          py: 0.8,
          borderRadius: "8px",
          border: "1px solid color-mix(in srgb, var(--text-primary) 5%, transparent)",
          background: "color-mix(in srgb, var(--text-primary) 2%, transparent)",
        }}
      >
        <Typography
          sx={{
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.88rem",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            color: "color-mix(in srgb, var(--text-primary) 40%, transparent)",
            fontSize: "0.72rem",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {label}
        </Typography>
        {trend && (
          <Box
            sx={{
              px: 0.8,
              py: 0.2,
              borderRadius: "4px",
              fontSize: "0.65rem",
              fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
              color: isPositiveTrend ? "#10b981" : "#ef4444",
              background: isPositiveTrend
                ? "rgba(16, 185, 129, 0.1)"
                : "rgba(239, 68, 68, 0.1)",
            }}
          >
            {trend}
          </Box>
        )}
      </Stack>
    </motion.div>
  );
};

export default KPIStat;
